import type { ClientOptions } from './types';
import { base } from './base';

interface ClientGlobalOptions {
  /**
   * Default headers applied to every request
   */
  headers: Record<string, string>;
  /**
   * Base URL to resolve relative URLs against
   *
   * Defaults to the current origin (window.location.origin)
   */
  baseURL?: string;
  /**
   * Error handler called when the client encounters an error
   * @param error - Error thrown by the client
   */
  onError: (error: unknown) => never;
  /**
   * Fetch implementation to use (defaults to global fetch)
   */
  fetch: typeof fetch;
}

const globalOptions: ClientGlobalOptions = {
  headers: {},
  onError: (error: unknown) => {
    throw error;
  },
  fetch,
};

export const client = async <TResponse = unknown>(
  input: string,
  options?: ClientOptions<TResponse>
): Promise<TResponse> => {
  const opts = options ?? {};

  const headers = opts.headers instanceof Headers ? opts.headers : new Headers(opts.headers);

  Object.entries(globalOptions.headers).forEach(([key, value]) => {
    if (!headers.has(key)) {
      headers.set(key, value);
    }
  });

  try {
    return await base<TResponse>(new URL(input, globalOptions.baseURL ?? window.location.origin), {
      ...opts,
      headers,
      fetch: globalOptions.fetch,
    });
  } catch (error) {
    globalOptions.onError(error);
  }
};

client.init = (options: Partial<ClientGlobalOptions>): void => {
  Object.defineProperties(globalOptions, Object.getOwnPropertyDescriptors(options));
};

client.getHeaders = (): Record<string, string> => {
  return { ...globalOptions.headers };
};
